"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { Loader, Calendar, Clock, Users, Phone, Mail, MapPin } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
const TIME_OPTIONS = ["12:00 PM", "12:30 PM", "13:00 PM", "13:30 PM", "14:00 PM", "18:00 PM", "18:30 PM", "19:00 PM", "19:30 PM", "20:00 PM", "20:30 PM", "21:00 PM"];
const inputClasses = "flex h-11 w-full rounded-none border border-border bg-transparent px-3 py-2 text-[12px] uppercase tracking-wider ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground/50 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-foreground focus-visible:ring-offset-0 disabled:cursor-not-allowed disabled:opacity-50 transition-colors";
function ReservationFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div data-section-id="221907" className="flex flex-col items-center justify-center py-12 text-center space-y-4 animate-in fade-in duration-500">
        <div className="size-16 rounded-none bg-foreground/5 flex items-center justify-center mb-4 border border-border">
          <SafeHydrate><Calendar className="size-8 text-foreground" /></SafeHydrate>
        </div>
        <h3 className="text-2xl font-default font-bold uppercase tracking-tighter text-foreground">Demande Reçue</h3>
        <p className="text-muted-foreground max-w-md mx-auto font-default text-sm">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-6 rounded-none border-border uppercase tracking-[0.15em] text-[10px]">
          Faire une autre demande
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <FormField control={control} name="name" rules={{
        required: "Le nom est requis"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-[12px] uppercase tracking-[0.15em] font-default">Nom Complet</FormLabel>
              <FormControl>
                <input type="text" className={inputClasses} placeholder="JEAN DUPONT" {...field} />
              </FormControl>
              <FormMessage className="text-[10px] uppercase" />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Le courriel est requis",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Format de courriel invalide"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-[12px] uppercase tracking-[0.15em] font-default">Adresse Courriel</FormLabel>
              <FormControl>
                <input type="email" className={inputClasses} placeholder="JEAN@EXEMPLE.COM" {...field} />
              </FormControl>
              <FormMessage className="text-[10px] uppercase" />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        required: "Le téléphone est requis",
        pattern: {
          value: /^\+?[0-9\s\-()]+$/,
          message: "Format de téléphone invalide"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-[12px] uppercase tracking-[0.15em] font-default">Téléphone</FormLabel>
              <FormControl>
                <input type="tel" className={inputClasses} placeholder="+1 418 000 0000" {...field} />
              </FormControl>
              <FormMessage className="text-[10px] uppercase" />
            </FormItem>} />

        <FormField control={control} name="guests" rules={{
        required: "Le nombre de voyageurs est requis",
        min: {
          value: 1,
          message: "Minimum 1 voyageur"
        },
        max: {
          value: 20,
          message: "Maximum 20 voyageurs"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-[12px] uppercase tracking-[0.15em] font-default">Nombre de Voyageurs</FormLabel>
              <FormControl>
                <input type="number" min="1" max="20" className={inputClasses} placeholder="2" {...field} />
              </FormControl>
              <FormMessage className="text-[10px] uppercase" />
            </FormItem>} />

        <FormField control={control} name="date" rules={{
        required: "La date est requise"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-[12px] uppercase tracking-[0.15em] font-default">Date d'arrivée</FormLabel>
              <FormControl>
                <input type="date" className={inputClasses} {...field} />
              </FormControl>
              <FormMessage className="text-[10px] uppercase" />
            </FormItem>} />

        <FormField control={control} name="time" rules={{
        required: "L'heure est requise"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-[12px] uppercase tracking-[0.15em] font-default">Heure d'arrivée estimée</FormLabel>
              <FormControl>
                <select className={inputClasses} {...field}>
                  <option value="" disabled>Choisir un créneau</option>
                  {TIME_OPTIONS.map((time, i) => <option key={time} value={time} data-index={i}>
                      {time}
                    </option>)}
                </select>
              </FormControl>
              <FormMessage className="text-[10px] uppercase" />
            </FormItem>} />
      </div>

      <FormField control={control} name="special_requests" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-[12px] uppercase tracking-[0.15em] font-default">Notes Particulières</FormLabel>
            <FormControl>
              <Textarea placeholder="Détails sur votre séjour ou demandes spécifiques..." className="min-h-[120px] resize-none rounded-none border-border bg-transparent text-[12px] placeholder:text-muted-foreground/50 focus-visible:ring-1 focus-visible:ring-foreground focus-visible:ring-offset-0" {...field} />
            </FormControl>
            <FormDescription className="text-[10px] uppercase tracking-wide opacity-50">
              Optionnel : aidez-nous à préparer votre immersion.
            </FormDescription>
            <FormMessage className="text-[10px] uppercase" />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-none text-destructive text-[10px] uppercase tracking-wider">
          {submitError}
        </div>}

      <Button type="submit" size="lg" disabled={isSubmitting} className="w-full md:w-auto min-w-[240px] rounded-none bg-foreground text-background hover:bg-foreground/90 uppercase tracking-[0.2em] text-[11px] font-default py-6 transition-all">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-4" />
            TRAITEMENT...
          </> : 'SÉCURISER MON SÉJOUR'}
      </Button>
    </Form>;
}
export default function SecureYourTable() {
  return;
}